import { useReactiveVar } from '@apollo/client'
import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Skeleton from '@material-ui/lab/Skeleton'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { ReplacementOption } from 'components/WorkspaceEditor/components/NodeContents'
import React, { useMemo } from 'react'
import { DESIGNATION_MAX_LENGTH, DESIGNATION_PLACEHOLDER } from '../constants'
import { useDrawingDesignationHandlers } from '../hooks'
import { parseDesignationText } from '../services/textParser'
import { useDesignationFieldStyles } from './useDesignationFieldStyles'

interface DesignationFieldProps {
    keyedText: string
    keyedTextValue?: string
    contentItemId: string
    editable: boolean
    inactive: boolean
    showPrefix?: boolean
    showSuffix?: boolean
}

/**
 * Product Type designation field with inline editing.
 * Format: "{prefix} ({designation}): {value}"
 * Example: "A. Clear Glass (DD-001): ASTM C1036"
 */
export const DesignationField = ({
    keyedText,
    keyedTextValue,
    contentItemId,
    editable,
    inactive,
    showPrefix = true,
    showSuffix = true,
}: DesignationFieldProps) => {
    const classes = useDesignationFieldStyles()
    const { isReviewer } = useReactiveVar(userInfo)
    const { prefix: textPrefix, designation: designationValue } = useMemo(
        () => parseDesignationText(keyedText),
        [keyedText]
    )

    const designationContentItem: Partial<TextContentElement> = useMemo(
        () => ({
            id: `${contentItemId}-drawing-designation`,
            text: designationValue,
            placeholderText: DESIGNATION_PLACEHOLDER,
            type: 'ReplacementOption',
        }),
        [contentItemId, designationValue]
    )

    const { handleValueChange, isSaving, canEditDesignation } = useDrawingDesignationHandlers({
        currentDesignation: designationValue,
        contentItemId,
    })

    return (
        <>
            {showPrefix && textPrefix}
            {canEditDesignation && (
                <span className={classes.container}>
                    <span className={clsx(classes.content, isSaving ? classes.contentHidden : classes.contentVisible)}>
                        <ReplacementOption
                            contentItem={designationContentItem}
                            editable={editable && !isSaving && !isReviewer}
                            inactive={inactive}
                            onValueChange={handleValueChange}
                            component="span"
                            maxLength={DESIGNATION_MAX_LENGTH}
                        />
                    </span>
                    {isSaving && (
                        <Skeleton
                            variant="rect"
                            animation="wave"
                            className={classes.skeleton}
                        />
                    )}
                </span>
            )}
            {showSuffix && `:${keyedTextValue ?? ''}`}
        </>
    )
}
